import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import { Button, Chip, Grid, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useState } from "react";

import {
  exportExecutiveDailyBriefExcel,
  exportExecutiveDailyBriefPdf,
} from "../../../../../../infrastructure/api/managementDashboard";
import { formatNumber } from "../../../../../../infrastructure/utils/formatters";
import { ManagementWidgetShell, severityChipColor, severityLabel } from "../managementDashboardWidgets";
import { buildDrillDownHref, isApiSuccess, pick } from "../managementDashboardUtils";

const openExportLink = (payload) => {
  const link = payload?.link || payload?.Link;
  if (link) window.open(link, "_blank", "noopener,noreferrer");
};

const ExecutiveBriefWidget = ({
  loading,
  error,
  data,
  onRetry,
  dashboardFilters,
  canExport = true,
}) => {
  const [exporting, setExporting] = useState(false);
  const changes = data?.majorChanges || data?.MajorChanges || [];
  const issues = data?.criticalIssues || data?.CriticalIssues || [];
  const actions = data?.recommendedActions || data?.RecommendedActions || [];

  const handleExport = async (type) => {
    try {
      setExporting(true);
      const res = type === "pdf"
        ? await exportExecutiveDailyBriefPdf(dashboardFilters || {})
        : await exportExecutiveDailyBriefExcel(dashboardFilters || {});
      if (isApiSuccess(res)) openExportLink(res.data);
    } finally {
      setExporting(false);
    }
  };

  return (
    <ManagementWidgetShell
      title="خلاصه روزانه اجرایی"
      subheader="تغییرات مهم، مسائل بحرانی و اقدامات پیشنهادی"
      loading={loading}
      error={error}
      onRetry={onRetry}
    >
      {canExport ? (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<TableViewOutlinedIcon />}
            disabled={exporting}
            onClick={() => handleExport("excel")}
          >
            خروجی Excel
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PictureAsPdfOutlinedIcon />}
            disabled={exporting}
            onClick={() => handleExport("pdf")}
          >
            خروجی PDF
          </Button>
        </Stack>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">سلامت</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="h4" fontWeight={800}>
              {pick(data, "healthScore", "HealthScore") != null
                ? formatNumber(pick(data, "healthScore", "HealthScore"), 0)
                : "—"}
            </Typography>
            <Chip
              size="small"
              color={severityChipColor(pick(data, "healthSeverity", "HealthSeverity"))}
              label={severityLabel(pick(data, "healthSeverity", "HealthSeverity"))}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {pick(data, "healthSummary", "HealthSummary")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography fontWeight={600} fontSize={14} sx={{ mb: 1 }}>تغییرات مهم</Typography>
          <Stack spacing={0.75}>
            {changes.map((c) => (
              <Stack key={pick(c, "code", "Code")} direction="row" justifyContent="space-between">
                <Typography fontSize={13}>{pick(c, "title", "Title")}</Typography>
                <Typography
                  fontSize={13}
                  fontWeight={700}
                  color={(pick(c, "percentageChange", "PercentageChange") ?? 0) >= 0 ? "success.main" : "error.main"}
                >
                  {pick(c, "percentageChange", "PercentageChange") != null
                    ? `${formatNumber(pick(c, "percentageChange", "PercentageChange"), 1)}٪`
                    : "—"}
                </Typography>
              </Stack>
            ))}
            {!changes.length && !loading ? (
              <Typography variant="caption" color="text.secondary">تغییر معناداری ثبت نشده.</Typography>
            ) : null}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography fontWeight={600} fontSize={14} sx={{ mb: 1 }}>مسائل بحرانی</Typography>
          <Stack spacing={0.75}>
            {issues.slice(0, 5).map((issue) => {
              const href = buildDrillDownHref(pick(issue, "drillDown", "DrillDown"));
              return (
                <Stack key={pick(issue, "code", "Code")} direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    color={severityChipColor(pick(issue, "severity", "Severity"))}
                    label={formatNumber(pick(issue, "count", "Count"))}
                  />
                  {href ? (
                    <Link component={RouterLink} to={href} fontSize={13}>{pick(issue, "title", "Title")}</Link>
                  ) : (
                    <Typography fontSize={13}>{pick(issue, "title", "Title")}</Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Typography fontWeight={600} fontSize={14} sx={{ mb: 1 }}>اقدامات پیشنهادی</Typography>
          <Stack spacing={0.75}>
            {actions.map((a, idx) => (
              <Typography key={pick(a, "actionId", "ActionId") ?? idx} fontSize={13}>
                • {pick(a, "title", "Title")}
              </Typography>
            ))}
            {!actions.length && !loading ? (
              <Typography variant="caption" color="text.secondary">اقدام باز برای امروز ثبت نشده.</Typography>
            ) : null}
          </Stack>
        </Grid>
      </Grid>
    </ManagementWidgetShell>
  );
};

export default ExecutiveBriefWidget;
